/**
 * FGA Entity Type Prefixes
 * 
 * OpenFGA uses typed identifiers with prefixes to distinguish entity types.
 * These constants ensure consistency across the application.
 * 
 * @see https://openfga.dev/docs/modeling/building-blocks/users
 * @packageDocumentation
 */

/**
 * Prefix for user entities in FGA.
 * Format: `user:{provider}|{organisationId}|{userId}`
 * 
 * @example "user:waad|org_demo|abc123-def456"
 */
export const FGA_USER_PREFIX = 'user:';

/**
 * Prefix for group entities in FGA.
 * Format: `group:{organisationId}|{groupName}` or `group:{groupName}`
 * 
 * @example "group:org_demo|engineering"
 * @example "group:engineering"
 */
export const FGA_GROUP_PREFIX = 'group:';

/**
 * Prefix for organization entities in FGA.
 * Format: `organization:{organizationId}`
 * 
 * Note: Uses US English spelling "organization" per OpenFGA conventions.
 * 
 * @example "organization:org-test1"
 * @example "organization:76e3921f-489b-4b7e-9547-9ea297add9b5"
 */
export const FGA_ORGANIZATION_PREFIX = 'organization:';

/**
 * Creates a fully qualified FGA user identifier.
 * 
 * @param userId - The user ID (without prefix)
 * @returns Fully qualified user identifier
 * 
 * @example
 * ```typescript
 * createFgaUserId('waad|org_demo|abc123');
 * // Returns: 'user:waad|org_demo|abc123'
 * ```
 */
export function createFgaUserId(userId: string): string {
  return userId.startsWith(FGA_USER_PREFIX) ? userId : `${FGA_USER_PREFIX}${userId}`;
}

/**
 * Creates a fully qualified FGA group identifier.
 * 
 * @param groupId - The group ID (without prefix)
 * @returns Fully qualified group identifier
 * 
 * @example
 * ```typescript
 * createFgaGroupId('engineering');
 * // Returns: 'group:engineering'
 * ```
 */
export function createFgaGroupId(groupId: string): string {
  return groupId.startsWith(FGA_GROUP_PREFIX) ? groupId : `${FGA_GROUP_PREFIX}${groupId}`;
}

/**
 * Creates a fully qualified FGA organization identifier.
 * 
 * @param organizationId - The organization ID (without prefix)
 * @returns Fully qualified organization identifier
 * 
 * @example
 * ```typescript
 * createFgaOrganizationId('org-test1');
 * // Returns: 'organization:org-test1'
 * ```
 */
export function createFgaOrganizationId(organizationId: string): string {
  return organizationId.startsWith(FGA_ORGANIZATION_PREFIX) 
    ? organizationId 
    : `${FGA_ORGANIZATION_PREFIX}${organizationId}`;
}

/**
 * Determines whether an FGA user identifier refers to an M2M (machine-to-machine) client
 * rather than a human user.
 *
 * Human user IDs follow the format `user:{provider}|{org}|{userId}` (contain pipe separators).
 * M2M client IDs are stored as `user:{clientId}@clients` without pipe separators.
 *
 * @param fgaUserId - Full FGA user identifier (must start with 'user:')
 * @returns true if the ID represents an M2M client
 *
 * @example
 * ```typescript
 * isM2MClientUserId('user:abc123XYZ@clients'); // true (no pipes, @clients suffix)
 * isM2MClientUserId('user:abc123XYZ');         // true (no pipes, legacy format)
 * isM2MClientUserId('user:waad|org_demo|abc123'); // false (has pipes)
 * ```
 */
export function isM2MClientUserId(fgaUserId: string): boolean {
  if (!fgaUserId.startsWith(FGA_USER_PREFIX)) return false;
  const userId = fgaUserId.substring(FGA_USER_PREFIX.length);
  return !userId.includes('|');
}

/**
 * Extracts the Auth0 client_id from an M2M FGA user identifier.
 *
 * Strips the `user:` prefix and the `@clients` suffix (if present)
 * to return the raw Auth0 client_id.
 *
 * @param fgaUserId - Full FGA user identifier for an M2M client
 * @returns The Auth0 client_id
 *
 * @example
 * ```typescript
 * extractM2MClientId('user:abc123XYZ@clients'); // 'abc123XYZ'
 * extractM2MClientId('user:abc123XYZ');         // 'abc123XYZ'
 * ```
 */
export function extractM2MClientId(fgaUserId: string): string {
  const raw = fgaUserId.substring(FGA_USER_PREFIX.length);
  return raw.endsWith('@clients') ? raw.slice(0, -'@clients'.length) : raw;
}
